import type * as React from "react";

import { Chip } from "./Chip";
import { formatClockTime } from "./TimeLabel";
import { cn } from "../lib/cn";

export interface BrianBubbleHeard {
  speaker: string;
  time: Date | string;
  quote: string;
}

export type BrianBubbleVariant = "answer" | "asking";

export type BrianBubbleTail = "end" | "start";

const variantHalo: Record<BrianBubbleVariant, string> = {
  answer: "shadow-bubble",
  asking: "shadow-bubble-asking",
};

const tailSide: Record<BrianBubbleTail, string> = {
  end: "right-[30px]",
  start: "left-[30px]",
};

export interface BrianBubbleProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children"
> {
  heard?: BrianBubbleHeard;
  children: React.ReactNode;
  drawing?: React.ReactNode;
  checked?: React.ReactNode;
  actions?: React.ReactNode;
  variant?: BrianBubbleVariant;
  tail?: BrianBubbleTail;
  ref?: React.Ref<HTMLElement>;
}

/** Positioned by the consumer. */
export function BrianBubble({
  heard,
  children,
  drawing,
  checked,
  actions,
  variant = "answer",
  tail = "end",
  className,
  ...rest
}: BrianBubbleProps) {
  const heardAt =
    heard == null
      ? null
      : heard.time instanceof Date
        ? heard.time
        : new Date(heard.time);

  return (
    <section
      aria-label="Brian"
      aria-live="polite"
      {...rest}
      className={cn(
        "relative whitespace-normal rounded-bubble border-[1.5px] border-accent bg-card text-left",
        "px-[26px] pt-5 pb-[18px]",
        variantHalo[variant],
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute -bottom-2 size-3.5 rotate-45 border-r-[1.5px] border-b-[1.5px] border-accent bg-card",
          tailSide[tail],
        )}
      />
      {heard && heardAt && (
        <div>
          <p className="text-mono-caps text-xs text-annotation">
            Heard · {heard.speaker} ·{" "}
            <time
              dateTime={
                Number.isNaN(heardAt.getTime())
                  ? undefined
                  : heardAt.toISOString()
              }
            >
              {Number.isNaN(heardAt.getTime())
                ? String(heard.time)
                : formatClockTime(heardAt)}
            </time>
          </p>
          <p className="mt-1.5 font-hand text-2xl leading-[1.25] text-ink-body">
            &ldquo;{heard.quote}&rdquo;
          </p>
        </div>
      )}
      <div
        className={cn(
          "grid grid-cols-[auto_minmax(0,1fr)] gap-x-3.5 gap-y-2.5",
          heard && "mt-3.5 border-t border-rule pt-3.5",
        )}
      >
        <Chip tone="brian" size="lg" className="mt-[3px] self-start">
          Brian
        </Chip>
        <div className="font-display text-[1.625rem] leading-[1.2] tracking-[-0.01em] text-ink">
          {children}
        </div>
        {drawing != null && <div className="col-span-2 mt-1">{drawing}</div>}
        {checked != null && (
          <p className="col-start-2 text-mono-time text-[0.8125rem] text-annotation">
            Checked · {checked}
          </p>
        )}
        {actions != null && (
          <div className="col-start-2 mt-1.5 flex flex-wrap items-center gap-3">
            {actions}
          </div>
        )}
      </div>
    </section>
  );
}
